/**
 * admin-store — agora consome o backend real via React Query (Rodada Frente A).
 *
 * API pública INTACTA (mesmas funções `useUsers`, `useUserActions`,
 * `useDepartments`, `useDepartmentActions`) — consumidores existentes não
 * precisam mudar nada. Por baixo, React Query gerencia cache + revalidação.
 *
 * O `pending` (string | null) é mantido pra preservar a UX dos botões (loading
 * por ID). Cada mutation atualiza o estado local `pending` antes de chamar a
 * API e limpa em settle.
 *
 * Quando o user NÃO está autenticado (boot inicial, login screen, etc), as
 * queries não disparam (`enabled: false` via token check) — useUsers/etc
 * retornam array vazio em vez de quebrar.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';
import type { Department, Profile, User } from './admin-types';

// ─── Queries: chaves padronizadas ───────────────────────────────────────

const KEYS = {
  users: ['v1', 'users'] as const,
  usersLookup: ['v1', 'users', 'lookup'] as const,
  // CA4 (Migration 406): valores distintos de segmento (cliente_tags) pro
  // multi-select "Acesso ao cofre por segmento".
  segmentosClientes: ['v1', 'users', 'segmentos-clientes'] as const,
  departments: ['v1', 'departments'] as const,
  // FEAT-V2-037 Fase A: cache key dos perfis.
  profiles: ['v1', 'profiles'] as const,
  profile: (id: string) => ['v1', 'profile', id] as const,
};

// ─── USERS ──────────────────────────────────────────────────────────────

export function useUsers(): User[] {
  const { data } = useQuery({
    queryKey: KEYS.users,
    queryFn: () => api.get<User[]>('/api/v1/users'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
  return data ?? [];
}

/** Lista leve {id, nome, fotoUrl} de usuários internos ativos. */
export interface UserLookup {
  id: string;
  nome: string;
  fotoUrl: string | null;
}

/**
 * CA4 (Migration 406): lista os SEGMENTOS de cliente existentes (valores
 * distintos de `cliente_tags`), pra alimentar o multi-select "Acesso ao cofre
 * por segmento" no form de usuário. Admin-only no backend.
 */
export function useSegmentosClientes(): string[] {
  const { data } = useQuery({
    queryKey: KEYS.segmentosClientes,
    queryFn: () => api.get<string[]>('/api/v1/users/segmentos-clientes'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
  return data ?? [];
}

/**
 * CARD-VENDAS-VENDEDOR (Migration 491): vendedores do cache do Omie — uma
 * opção por NOME — pro Select "Vendedor no Omie" do form de usuário.
 * Admin-only no backend.
 */
export function useVendedoresOmie(): Array<{ codigo: string; nome: string }> {
  const { data } = useQuery({
    queryKey: ['admin', 'vendedores-omie'],
    queryFn: () => api.get<Array<{ codigo: string; nome: string }>>('/api/v1/users/vendedores-omie'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
  return data ?? [];
}

/**
 * Versão LEVE e NÃO-admin de `useUsers` (B2 Lote 3 #23 / Lote 4 #12).
 * Bate em `GET /api/v1/users/lookup` (acessível por qualquer autenticado) e
 * traz só {id, nome, fotoUrl}. Use pra resolver @menções por nome e fotos de
 * colaborador em telas que não-admins também acessam (comentários, cards) —
 * `useUsers()` retorna [] pra não-admin (endpoint admin-only).
 */
export function useUsersLookup(): UserLookup[] {
  const { data } = useQuery({
    queryKey: KEYS.usersLookup,
    queryFn: () => api.get<UserLookup[]>('/api/v1/users/lookup'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
  return data ?? [];
}

/**
 * O14 (rodada ORCAMENTO-CONSTRUCAO): usuários ativos do DEPARTAMENTO
 * comercial — regra do CRM: TODO dropdown de usuário do módulo comercial
 * (responsável de lead/proposta, vendedor de meta/ranking, atribuir de
 * automação) lista SÓ gente do comercial. Endpoint acessível por qualquer
 * autenticado (vendedor não-admin também cria proposta).
 */
export function useUsersComercial(): UserLookup[] {
  const { data } = useQuery({
    queryKey: ['v1', 'users', 'comercial'] as const,
    queryFn: () => api.get<UserLookup[]>('/api/v1/users/comercial'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
  return data ?? [];
}

/**
 * PF0-d (rodada PIPELINE-FLUXO): usuários ativos do comercial OU orçamentista.
 * Fonte dos seletores que os DOIS participam — responsável DA ATIVIDADE do card
 * e @menção (o orçamentista precisa ser atribuível/mencionável). NÃO usar para
 * responsável do CARD (esse segue só vendedor via `useUsersComercial`).
 */
export function useUsersComercialOrcamentista(): UserLookup[] {
  const { data } = useQuery({
    queryKey: ['v1', 'users', 'comercial-orcamentista'] as const,
    queryFn: () => api.get<UserLookup[]>('/api/v1/users/comercial-orcamentista'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
  return data ?? [];
}

/**
 * Resolve a foto de um usuário pelo ID. Útil pra renderizar avatar do
 * responsável em listas/cards sem prop drilling.
 *
 * Implementação: usa `useUsers()` (compartilhado via cache do React Query —
 * sem custo extra de rede). Retorna `undefined` se user não encontrado OU
 * se o caller não for admin (endpoint `/api/v1/users` é admin-only — pra
 * colaboradores retorna [] e o Avatar cai pras iniciais).
 *
 * Tech-debt: criar endpoint público `/api/v1/users/lookup` com lista leve
 * `{id, nome, fotoUrl}[]` pra colaboradores também verem fotos nos cards.
 */
export function useUserFotoUrl(userId: string | null | undefined): string | null | undefined {
  const users = useUsers();
  return useMemo(() => {
    if (!userId) return undefined;
    return users.find((u) => u.id === userId)?.fotoUrl;
  }, [users, userId]);
}

/**
 * I6b (revisão de avatares) — resolve a foto real de um usuário por ID usando
 * a lista LEVE `useUsersLookup()` (`/api/v1/users/lookup`, qualquer
 * autenticado). Retorna uma função `(id) => fotoUrl` estável pra passar `src`
 * ao `<Avatar>` em listas/cards onde o objeto local só traz `nome` (leads,
 * cards do funil, comentários, notificações) — SEM precisar alterar os
 * payloads/stores do CRM. Fora do lookup (ou id nulo) → `undefined` (o Avatar
 * cai pras iniciais).
 */
export function useFotoUrlPorId(): (userId: string | null | undefined) => string | null | undefined {
  const lookup = useUsersLookup();
  return useMemo(() => {
    const map = new Map(lookup.map((u) => [u.id, u.fotoUrl] as const));
    return (userId: string | null | undefined) => (userId ? map.get(userId) : undefined);
  }, [lookup]);
}

export function useUserActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidateUsers = () =>
    qc.invalidateQueries({ queryKey: KEYS.users });

  const createUser = useCallback(
    async (input: Omit<User, 'id' | 'criadoEmIso' | 'ultimoAcessoIso'>) => {
      setPending('create');
      try {
        const user = await api.post<User>('/api/v1/users', input);
        await invalidateUsers();
        return user;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const updateUser = useCallback(
    async (id: string, patch: Partial<User>) => {
      setPending(id);
      try {
        await api.put(`/api/v1/users/${id}`, patch);
        await invalidateUsers();
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const deleteUser = useCallback(
    async (id: string) => {
      setPending(id);
      try {
        await api.delete(`/api/v1/users/${id}`);
        await invalidateUsers();
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  return { createUser, updateUser, deleteUser, pending };
}

// ─── DEPARTMENTS ────────────────────────────────────────────────────────

/**
 * Lista completa dos departamentos (com `userCount`) — ADMIN-ONLY desde S-2.
 *
 * Use só em tela de Configurações. Para apenas traduzir slug → nome numa tela
 * de colaborador, use `useDepartmentsLookup` abaixo: este hook devolve 403 para
 * quem não é admin.
 */
export function useDepartments(): Department[] {
  const { data } = useQuery({
    queryKey: KEYS.departments,
    queryFn: () => api.get<Department[]>('/api/v1/departments'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
  return data ?? [];
}

export interface DepartmentLookupItem {
  slug: string;
  nome: string;
}

/**
 * S-2 (SEGURANCA-ACESSO-2) — slug + nome, aberto a qualquer autenticado.
 *
 * Existe porque `/perfil` é tela de TODO mundo e precisa escrever "Manutenção"
 * onde o vínculo guarda "manutencao". Sem isto, fechar a lista completa faria a
 * tela de perfil de quem não é admin passar a mostrar slug cru — perder acesso
 * a um dado que hoje aparece, o que a regra 11 da rodada proíbe.
 */
export function useDepartmentsLookup(): DepartmentLookupItem[] {
  const { data } = useQuery({
    queryKey: [...KEYS.departments, 'lookup'] as const,
    queryFn: () => api.get<DepartmentLookupItem[]>('/api/v1/departments/lookup'),
    enabled: hasToken(),
    staleTime: 5 * 60_000, // nome de setor não muda no meio do expediente
  });
  return data ?? [];
}

export function useDepartmentActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidateDepartments = () =>
    qc.invalidateQueries({ queryKey: KEYS.departments });

  const createDepartment = useCallback(
    async (input: Omit<Department, 'id' | 'userCount'>) => {
      setPending('create');
      try {
        const dept = await api.post<Department>('/api/v1/departments', input);
        await invalidateDepartments();
        return dept;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const updateDepartment = useCallback(
    async (id: string, patch: Partial<Department>) => {
      setPending(id);
      try {
        // id no v2 é o slug (servimos como id pra compat com frontend)
        await api.put(`/api/v1/departments/${id}`, patch);
        await invalidateDepartments();
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const deleteDepartment = useCallback(
    async (id: string) => {
      setPending(id);
      try {
        await api.delete(`/api/v1/departments/${id}`);
        await invalidateDepartments();
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  return { createDepartment, updateDepartment, deleteDepartment, pending };
}

// ─── PROFILES (FEAT-V2-037 Fase A) ──────────────────────────────────────

/**
 * Lista os 3 perfis internos (comercial / administrativo / tecnico).
 *
 * Endpoint público (`requireAuth` apenas — não admin-only) — qualquer user
 * autenticado consome pra preencher selects, mostrar label do perfil no
 * próprio /perfil, etc. PUT em /profiles/:id continua admin-only.
 */
export function useProfiles(): Profile[] {
  const { data } = useQuery({
    queryKey: KEYS.profiles,
    queryFn: () => api.get<Profile[]>('/api/v1/profiles'),
    enabled: hasToken(),
    staleTime: 5 * 60_000, // 5 min — perfis mudam raramente (admin edit)
  });
  return data ?? [];
}

export function useProfile(id: string | null | undefined) {
  return useQuery({
    queryKey: KEYS.profile(id ?? ''),
    queryFn: () => api.get<Profile>(`/api/v1/profiles/${encodeURIComponent(id!)}`),
    enabled: hasToken() && !!id,
    staleTime: 5 * 60_000,
  });
}

export function useProfileActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: KEYS.profiles });

  const updateProfile = useCallback(
    async (
      id: string,
      patch: { nome?: string; descricao?: string | null; permissionOverrides?: Record<string, 0 | 1> },
    ) => {
      setPending(id);
      try {
        const updated = await api.put<Profile>(
          `/api/v1/profiles/${encodeURIComponent(id)}`,
          patch,
        );
        await Promise.all([
          invalidate(),
          qc.invalidateQueries({ queryKey: KEYS.profile(id) }),
        ]);
        return updated;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc],
  );

  return { updateProfile, pending };
}

export type { Department, Profile, User };
